import React from "react";

interface IProps {
  data: any;
}

interface PerformanceMetrics {
  totalTickets: number;
  withinSL: number;
  withoutSL: number;
  slRate: number;
}

const getPerformanceMetrics = (data: any): PerformanceMetrics => {
  if (!data) {
    return { totalTickets: 0, withinSL: 0, withoutSL: 0, slRate: 0 };
  }

  const parseCount = (value: unknown) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  if (data.card_count) {
    const cards = data.card_count;
    const totalTickets = parseCount(cards.total);
    const withinSL = parseCount(cards.within_sl);
    const withoutSL = parseCount(cards.sl_breaches);
    const slRate = parseCount(cards.avg_sl_rate);

    return { totalTickets, withinSL, withoutSL, slRate };
  }

  const directTotal =
    data.total_ticket_count ??
    data.total_tickets_count ??
    (typeof data.total_tickets === "number" ? data.total_tickets : null);

  let withinSL = parseCount(
    data.within_sl ?? data.within_sla ?? data.sla_met_count,
  );
  let withoutSL = parseCount(
    data.without_sl ?? data.without_sla ?? data.sla_breached_count,
  );

  const arr = data.resolution_beyond_sla;
  if (Array.isArray(arr) && arr.length > 0) {
    withinSL = 0;
    withoutSL = 0;
    arr.forEach((item: any) => {
      const name = String(
        item?.name ?? item?.label ?? item?.status ?? "",
      ).toLowerCase();
      const value = parseCount(item?.value ?? item?.count);
      if (/within|met/.test(name) && !/without/.test(name)) {
        withinSL = value;
      } else if (/without|breach|beyond/.test(name)) {
        withoutSL = value;
      }
    });
  }

  const totalTickets =
    directTotal != null ? parseCount(directTotal) : withinSL + withoutSL;

  const slRate =
    data.sl_rate != null
      ? Math.round(parseCount(data.sl_rate))
      : totalTickets > 0
        ? Math.round((withinSL / totalTickets) * 100)
        : 0;

  return { totalTickets, withinSL, withoutSL, slRate };
};

const TotalTicketsIcon = ({ color }: { color: string }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const WithinSlIcon = ({ color }: { color: string }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
);

const BreachedSlIcon = ({ color }: { color: string }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

const SlRateIcon = ({ color }: { color: string }) => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="20" x2="18" y2="10" />
    <line x1="12" y1="20" x2="12" y2="4" />
    <line x1="6" y1="20" x2="6" y2="14" />
  </svg>
);

const PerformanceSummaryCards: React.FC<IProps> = ({ data }) => {
  const metrics = getPerformanceMetrics(data);

  const cards = [
    {
      label: "Total tickets",
      value: metrics.totalTickets.toLocaleString(),
      backgroundColor: "#f5f3ff",
      iconBg: "rgba(124, 58, 237, 0.18)",
      iconColor: "#7c3aed",
      valueColor: "#6d28d9",
      icon: TotalTicketsIcon,
    },
    {
      label: "Within SL",
      value: metrics.withinSL.toLocaleString(),
      backgroundColor: "#f0fdf4",
      iconBg: "rgba(22, 163, 74, 0.2)",
      iconColor: "#16a34a",
      valueColor: "#166534",
      icon: WithinSlIcon,
    },
    {
      label: "Breached SL",
      value: metrics.withoutSL.toLocaleString(),
      backgroundColor: "#fff7ed",
      iconBg: "rgba(234, 88, 12, 0.18)",
      iconColor: "#ea580c",
      valueColor: "#c2410c",
      icon: BreachedSlIcon,
    },
    {
      label: "SL rate",
      value: `${metrics.slRate}%`,
      backgroundColor: "#ecfeff",
      iconBg: "rgba(8, 145, 178, 0.18)",
      iconColor: "#0891b2",
      valueColor: "#0e7490",
      icon: SlRateIcon,
    },
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(4, 1fr)",
        gap: "16px",
        marginBottom: "8px",
        width: "100%",
      }}
    >
      {cards.map((card) => {
        const Icon = card.icon;
        return (
          <div
            key={card.label}
            style={{
              backgroundColor: card.backgroundColor,
              borderRadius: "16px",
              boxShadow: "0 1px 3px rgba(0, 0, 0, 0.06)",
              padding: "20px 20px",
              position: "relative",
              minHeight: "110px",
              width: "100%",
              boxSizing: "border-box",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "12px",
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: "12px",
                  fontWeight: 600,
                  color: "#64748b",
                  letterSpacing: "0.05em",
                  marginBottom: "6px",
                }}
              >
                {card.label}
              </div>
              <div
                style={{
                  fontSize: "28px",
                  fontWeight: 700,
                  color: card.valueColor,
                  letterSpacing: "-0.02em",
                  lineHeight: 1.2,
                }}
              >
                {card.value}
              </div>
            </div>
            <div
              style={{
                width: "44px",
                height: "44px",
                borderRadius: "12px",
                backgroundColor: card.iconBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <Icon color={card.iconColor} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PerformanceSummaryCards;
